import React from 'react'
import { Link } from 'react-router-dom'
import {
  DollarSign,
  Eye,
  FileText,
  TrendingUp,
  Briefcase,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Star,
  Calendar
} from 'lucide-react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { mockOpportunities, formatNumber, formatCurrency } from '../../data/mockData'

const stats = [
  { name: 'Total Earnings', value: '$24,500', change: '+$5,200 this month', icon: DollarSign, color: 'bg-green-500' },
  { name: 'Total Reach', value: '2.8M', change: '+18% vs last month', icon: Eye, color: 'bg-blue-500' },
  { name: 'Active Campaigns', value: '3', change: '2 pending delivery', icon: Briefcase, color: 'bg-purple-500' },
  { name: 'Avg. Engagement', value: '5.2%', change: 'Above average', icon: TrendingUp, color: 'bg-teal-500' },
]

const earningsData = [
  { month: 'Aug', earnings: 3200 },
  { month: 'Sep', earnings: 4100 },
  { month: 'Oct', earnings: 3800 },
  { month: 'Nov', earnings: 4500 },
  { month: 'Dec', earnings: 3900 },
  { month: 'Jan', earnings: 5200 },
]

const activeDeliverables = [
  {
    id: '1',
    campaign: 'Spring Wellness Launch',
    brand: 'MedTech Innovations',
    type: 'Instagram Reel',
    dueDate: '2026-02-15',
    status: 'draft_submitted',
    fee: 5000,
  },
  {
    id: '2',
    campaign: 'Spring Wellness Launch',
    brand: 'MedTech Innovations',
    type: 'TikTok Video',
    dueDate: '2026-02-20',
    status: 'pending',
    fee: 5000,
  },
  {
    id: '3',
    campaign: 'Heart Health Awareness',
    brand: 'CardioFirst',
    type: 'Instagram Carousel',
    dueDate: '2026-02-25',
    status: 'revisions_requested',
    fee: 3500,
  },
]

const statusLabels = {
  pending: { label: 'Not Started', color: 'bg-gray-100 text-gray-700' },
  draft_submitted: { label: 'Draft Submitted', color: 'bg-blue-100 text-blue-700' },
  revisions_requested: { label: 'Revisions Needed', color: 'bg-yellow-100 text-yellow-700' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
  posted: { label: 'Posted', color: 'bg-emerald-100 text-emerald-700' },
}

function CreatorDashboard() {
  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-hf-teal-600 to-teal-600 rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Welcome back, Dr. Grunch!</h1>
            <p className="text-hf-teal-100">You have 2 new campaign opportunities and 1 deliverable pending review.</p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-3">
            <div className="bg-white/20 rounded-lg px-4 py-2">
              <div className="flex items-center">
                <Star className="w-5 h-5 text-yellow-300 fill-current mr-2" />
                <span className="font-bold">4.9</span>
                <span className="text-hf-teal-100 ml-1">rating</span>
              </div>
            </div>
            <div className="bg-white/20 rounded-lg px-4 py-2">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="font-bold">SOFI Verified</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.name} className="stat-card">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-sm text-gray-500">{stat.name}</p>
            <p className="text-xs text-green-600 mt-1">{stat.change}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Earnings Chart */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Earnings Overview</h2>
            <select className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white">
              <option>Last 6 months</option>
              <option>Last 12 months</option>
              <option>This year</option>
            </select>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={earningsData}>
                <defs>
                  <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(val) => `$${val/1000}k`} />
                <Tooltip
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Earnings']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Area
                  type="monotone"
                  dataKey="earnings"
                  stroke="#10b981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorEarnings)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* New Opportunities */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">New Opportunities</h2>
            <span className="badge bg-red-100 text-red-700">2 new</span>
          </div>
          <div className="space-y-4">
            {mockOpportunities.map((opp) => (
              <div key={opp.id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-gray-900">{opp.campaignName}</p>
                  <span className="text-sm font-medium text-green-600">{opp.budget}</span>
                </div>
                <p className="text-sm text-gray-500 mb-2">{opp.brand.name}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-xs text-gray-400">
                    <Calendar className="w-3 h-3 mr-1" />
                    Due {new Date(opp.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <span className="text-xs font-medium text-hf-teal-600">{opp.matchScore}% match</span>
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/creator/opportunities"
            className="flex items-center justify-center w-full mt-4 text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium"
          >
            View all opportunities
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      </div>

      {/* Active Deliverables */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Active Deliverables</h2>
          <Link to="/creator/deliverables" className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center">
            View all
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Campaign</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Deliverable</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Due Date</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Fee</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {activeDeliverables.map((del) => {
                const status = statusLabels[del.status] || statusLabels.pending
                const isOverdue = new Date(del.dueDate) < new Date()

                return (
                  <tr key={del.id} className="hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium text-gray-900">{del.campaign}</p>
                        <p className="text-sm text-gray-500">{del.brand}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-gray-600">{del.type}</td>
                    <td className="py-4 px-4">
                      <div className={`flex items-center ${isOverdue ? 'text-red-600' : 'text-gray-600'}`}>
                        {isOverdue && <AlertCircle className="w-4 h-4 mr-1" />}
                        {new Date(del.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`badge ${status.color}`}>{status.label}</span>
                    </td>
                    <td className="py-4 px-4 font-medium text-gray-900">{formatCurrency(del.fee)}</td>
                    <td className="py-4 px-4 text-right">
                      {del.status === 'pending' && (
                        <button className="btn-primary text-sm py-1.5">Start</button>
                      )}
                      {del.status === 'draft_submitted' && (
                        <span className="text-sm text-gray-500">Awaiting review</span>
                      )}
                      {del.status === 'revisions_requested' && (
                        <button className="btn-primary text-sm py-1.5">Edit Draft</button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Link
          to="/creator/opportunities"
          className="card hover:border-hf-teal-200 hover:shadow-md transition-all group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-hf-teal-100 rounded-xl flex items-center justify-center group-hover:bg-hf-teal-200 transition-colors">
              <Briefcase className="w-6 h-6 text-hf-teal-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Browse Opportunities</p>
              <p className="text-sm text-gray-500">Find new campaigns</p>
            </div>
          </div>
        </Link>
        <Link
          to="/creator/deliverables"
          className="card hover:border-teal-200 hover:shadow-md transition-all group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center group-hover:bg-teal-200 transition-colors">
              <FileText className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Submit Content</p>
              <p className="text-sm text-gray-500">Upload deliverables</p>
            </div>
          </div>
        </Link>
        <Link
          to="/creator/profile"
          className="card hover:border-purple-200 hover:shadow-md transition-all group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Update Profile</p>
              <p className="text-sm text-gray-500">Improve visibility</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  )
}

export default CreatorDashboard
